import type { QuotaLimits } from '../core/quota';
import { analysisErrorMessage, REPORT_LOAD_FAILED, uploadErrorMessage } from '../core/run-messages';
import {
  objectivesAnalysisFailed,
  objectivesUploadRejected,
  objectivesUploadStarted,
  objectivesUploadSucceeded,
  previewObjectivesAction,
  type AnalyticsEvent,
} from '../core/events';
import type { ApiResult, ReportBody } from './api';

/** Everything the run touches outside itself. The page supplies the real ones; tests fake them. */
export interface RunContext {
  call: <T>(path: string, init?: RequestInit) => Promise<ApiResult<T>>;
  track: (event: AnalyticsEvent) => void;
  refreshUsage: () => void;
  now: () => number;
  variant: string;
  facts: AnalyticsEvent['properties'];
  limits: QuotaLimits;
}

export type RunResult =
  | { ok: true; report: ReportBody & { analysis: NonNullable<ReportBody['analysis']> } }
  | { ok: false; message: string };

/** Upload a plan, read it, and fetch the report: the three calls behind one click. */
export async function uploadAndRead(file: File, ctx: RunContext): Promise<RunResult> {
  const { call, track, variant, limits } = ctx;
  const startedAt = ctx.now();
  const facts = { ...ctx.facts, experiment_variant: variant };
  const elapsed = () => Math.round(ctx.now() - startedAt);
  const runFailed = (stage: string, reason: string) =>
    track(previewObjectivesAction('run_failed', { ...facts, stage, reason, duration_ms: elapsed() }));
  const kind = file.type || 'unknown';

  track(previewObjectivesAction('run_clicked', facts));
  track(objectivesUploadStarted(kind, variant));

  const form = new FormData();
  form.set('file', file);
  const created = await call<{ documentId: string }>('/api/documents', { method: 'POST', body: form });
  ctx.refreshUsage();
  if (!created.ok) {
    track(objectivesUploadRejected(created.reason ?? String(created.status)));
    runFailed('upload', created.reason ?? created.error);
    return { ok: false, message: uploadErrorMessage(created, limits) };
  }
  track(objectivesUploadSucceeded(kind, file.size, variant));

  const documentPath = `/api/documents/${created.data.documentId}`;
  const analyzed = await call<{ analysisId: string }>(`${documentPath}/analyze`, { method: 'POST' });
  ctx.refreshUsage();
  if (!analyzed.ok) {
    track(objectivesAnalysisFailed(analyzed.reason ?? String(analyzed.status)));
    runFailed('analysis', analyzed.reason ?? analyzed.error);
    return { ok: false, message: analysisErrorMessage(analyzed, limits) };
  }

  const fetched = await call<ReportBody>(documentPath);
  if (!fetched.ok || !fetched.data.analysis) {
    runFailed('report', fetched.ok ? 'missing_analysis' : fetched.error);
    return { ok: false, message: REPORT_LOAD_FAILED };
  }
  const analysis = fetched.data.analysis;
  track(previewObjectivesAction('run_completed', {
    ...facts, duration_ms: elapsed(),
    objective_count: analysis.segmentCount,
    finding_count: analysis.findings.length,
  }));
  return { ok: true, report: { ...fetched.data, analysis } };
}
